import React, { useState, useEffect } from "react";
import api from "../services/api";
import {
  MapPinIcon,
  RefreshCwIcon,
  ShieldCheckIcon
} from "../components/Icons";

const DEFAULT_CATEGORIES = [
  "Fashion & Lifestyle",
  "Footwear & Shoes",
  "Clothing & Apparel",
  "Electronics & Mobile Phones",
  "Computers & Accessories",
  "Home, Kitchen & Living",
  "Beauty, Cosmetics & Personal Care",
  "Health, Wellness & Pharmacy",
  "Groceries, Gourmet & Food",
  "Sports, Fitness & Outdoor Gear",
  "Jewelry, Watches & Eyewear",
  "Bags, Luggage & Backpacks",
  "Toys, Baby Products & Kids Fashion",
  "Books, Stationery & Office Supplies",
  "Automotive, Bikes & Accessories",
  "Pet Supplies & Pet Care",
  "Hardware, Electrical & Tools",
  "Handicrafts, Gifts & Art",
  "Furniture & Home Decor",
  "Musical Instruments",
  "Industrial & Scientific"
];

function StorePage({ user }) {
  const [storeExists, setStoreExists] = useState(false);
  const [activeSection, setActiveSection] = useState("basic");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  const [form, setForm] = useState({
    name: "",
    slug: "",
    description: "",
    category: "Fashion & Lifestyle",
    subcategory: "",
    businessType: "Physical Store",
    brandStory: "",
    yearsInBusiness: 1,
    specialization: "",
    businessHours: "Mon - Sat: 10:00 AM - 8:00 PM",
    openTime: "10:00",
    closeTime: "20:00",
    operatingDays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    isManualClosed: false,
    isVerified: false,
    isTrusted: false,
    location: { type: "Point", coordinates: [75.8302, 30.6826] },
    godownAddress: { hasGodown: false, contactName: "", phone: "", street: "", city: "", state: "", pincode: "" },
    logo: { imageurl: "" },
    banner: { imageurl: "" },
    gallery: [],
    contact: { email: "", phone: "" },
    address: { street: "", city: "", state: "", pincode: "" },
    pickupAddress: { contactName: "", phone: "", street: "", city: "", state: "", pincode: "", isSameAsBusiness: true },
    returnAddress: { contactName: "", phone: "", street: "", city: "", state: "", pincode: "", isSameAsPickup: true },
    socialLinks: { instagram: "", facebook: "", youtube: "", website: "" },
  });

  const [gpsStatus, setGpsStatus] = useState("idle"); // idle | detecting | detected | error
  const [gpsMessage, setGpsMessage] = useState("");

  const detectStoreLocation = async () => {
    if (!navigator.geolocation) {
      setGpsStatus("error");
      setGpsMessage("Geolocation not supported by this browser.");
      return;
    }
    setGpsStatus("detecting");
    setGpsMessage("Detecting exact shop coordinates...");

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setForm((prev) => ({
          ...prev,
          location: { type: "Point", coordinates: [lng, lat] }
        }));
        setGpsStatus("detected");

        // Reverse-geocode to address
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`, {
            headers: { "Accept-Language": "en" }
          });
          const data = await res.json();
          if (data?.address) {
            const a = data.address;
            const detectedCity = a.city || a.town || a.village || a.county || a.state_district || "";
            const detectedState = a.state || "";
            const detectedPincode = a.postcode || "";
            const detectedStreet = [a.road, a.suburb, a.neighbourhood].filter(Boolean).join(", ");

            setForm((prev) => ({
              ...prev,
              address: {
                ...prev.address,
                city: detectedCity || prev.address.city,
                state: detectedState || prev.address.state,
                pincode: detectedPincode || prev.address.pincode,
                street: detectedStreet || prev.address.street
              }
            }));
            setGpsMessage(`Shop pinned: ${detectedCity} (${lat.toFixed(4)}, ${lng.toFixed(4)})`);
          } else {
            setGpsMessage(`Shop pinned: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
          }
        } catch {
          setGpsMessage(`Shop pinned: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
        }
      },
      () => {
        setGpsStatus("error");
        setGpsMessage("Unable to fetch GPS. You can enter address manually.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const [newGalleryUrl, setNewGalleryUrl] = useState("");
  const [newGalleryTitle, setNewGalleryTitle] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [logoMode, setLogoMode] = useState("upload"); // "upload" | "url"
  const [bannerMode, setBannerMode] = useState("upload"); // "upload" | "url"
  const [galleryMode, setGalleryMode] = useState("upload"); // "upload" | "url"

  const fetchCategories = async () => {
    try {
      const res = await api.get("/categories/tree");
      if (res.data?.tree && res.data.tree.length > 0) {
        const treeNames = res.data.tree.map((cat) => cat.name).filter(Boolean);
        const merged = Array.from(new Set([...DEFAULT_CATEGORIES, ...treeNames]));
        setCategories(merged);
      }
    } catch (e) {
      try {
        const res2 = await api.get("/categories?level=1");
        if (res2.data?.categories && res2.data.categories.length > 0) {
          const l1Names = res2.data.categories.map((c) => c.name).filter(Boolean);
          const merged = Array.from(new Set([...DEFAULT_CATEGORIES, ...l1Names]));
          setCategories(merged);
        }
      } catch (err) {
        // Keep DEFAULT_CATEGORIES
      }
    }
  };

  const fetchStore = async () => {
    try {
      const response = await api.get("/vendor/store");
      const store = response.data.store;
      if (store) {
        setStoreExists(true);
        setForm({
          name: store.name || "",
          slug: store.slug || "",
          description: store.description || "",
          category: store.category || "Fashion & Lifestyle",
          subcategory: store.subcategory || "",
          businessType: store.businessType || "Physical Store",
          brandStory: store.brandStory || "",
          yearsInBusiness: store.yearsInBusiness || 1,
          specialization: store.specialization || "",
          businessHours: store.businessHours || "Mon - Sat: 10:00 AM - 8:00 PM",
          openTime: store.openTime || "10:00",
          closeTime: store.closeTime || "20:00",
          operatingDays: Array.isArray(store.operatingDays) && store.operatingDays.length > 0 ? store.operatingDays : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
          isManualClosed: Boolean(store.isManualClosed),
          isVerified: Boolean(store.isVerified),
          isTrusted: Boolean(store.isTrusted),
          location: store.location || { type: "Point", coordinates: [75.8302, 30.6826] },
          godownAddress: store.godownAddress || { hasGodown: false, contactName: "", phone: "", street: "", city: "", state: "", pincode: "" },
          logo: store.logo || { imageurl: "" },
          banner: store.banner || { imageurl: "" },
          gallery: Array.isArray(store.gallery) ? store.gallery : [],
          contact: { email: store.contact?.email || user.email || "", phone: store.contact?.phone || user.number || "" },
          address: { street: store.address?.street || "", city: store.address?.city || "", state: store.address?.state || "", pincode: store.address?.pincode || "" },
          pickupAddress: store.pickupAddress || { contactName: "", phone: "", street: "", city: "", state: "", pincode: "", isSameAsBusiness: true },
          returnAddress: store.returnAddress || { contactName: "", phone: "", street: "", city: "", state: "", pincode: "", isSameAsPickup: true },
          socialLinks: store.socialLinks || { instagram: "", facebook: "", youtube: "", website: "" },
        });
      }
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setStoreExists(false);
        setForm((prev) => ({
          ...prev,
          name: `${user.firstName || ""}'s Store`.trim(),
          contact: { email: user.email || "", phone: user.number || "" }
        }));
      } else {
        setError(err.response?.data?.msg || "Failed to load store settings.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStore();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);

    try {
      if (storeExists) {
        const response = await api.put("/vendor/store", form);
        setSuccess("Store profile updated successfully!");
        if (response.data.store) setForm((prev) => ({ ...prev, slug: response.data.store.slug }));
      } else {
        const response = await api.post("/vendor/store", form);
        setSuccess("Store created successfully!");
        setStoreExists(true);
        if (response.data.store) setForm((prev) => ({ ...prev, slug: response.data.store.slug }));
      }
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to save store details.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (file, target) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      return alert("Please select a valid image file (PNG, JPG, JPEG, WEBP).");
    }
    if (file.size > 10 * 1024 * 1024) {
      return alert("Image file size exceeds 10MB limit. Please choose a smaller image.");
    }

    const formData = new FormData();
    formData.append("image", file);

    try {
      if (target === "logo") setUploadingLogo(true);
      if (target === "banner") setUploadingBanner(true);
      if (target === "gallery") setUploadingGallery(true);

      const res = await api.post("/vendor/store/upload-media", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      const uploadedUrl = res.data?.imageUrl || res.data?.url || "";
      if (!uploadedUrl) throw new Error("Upload failed");

      if (target === "logo") {
        setForm((prev) => ({ ...prev, logo: { imageurl: uploadedUrl } }));
      } else if (target === "banner") {
        setForm((prev) => ({ ...prev, banner: { imageurl: uploadedUrl } }));
      } else if (target === "gallery") {
        setNewGalleryUrl(uploadedUrl);
      }
    } catch (err) {
      alert(err.response?.data?.msg || err.message || "Failed to upload image. Please try again.");
    } finally {
      if (target === "logo") setUploadingLogo(false);
      if (target === "banner") setUploadingBanner(false);
      if (target === "gallery") setUploadingGallery(false);
    }
  };

  const addGalleryImage = () => {
    if (!newGalleryUrl.trim()) return;
    setForm({
      ...form,
      gallery: [...form.gallery, { imageurl: newGalleryUrl.trim(), title: newGalleryTitle.trim() || "Store Photo" }]
    });
    setNewGalleryUrl("");
    setNewGalleryTitle("");
  };

  const removeGalleryImage = (index) => {
    setForm({
      ...form,
      gallery: form.gallery.filter((_, idx) => idx !== index)
    });
  };

  // Completion calculation
  const completionItems = [
    { label: "Store Name", done: !!form.name },
    { label: "Description", done: !!form.description },
    { label: "Store Logo", done: !!form.logo?.imageurl },
    { label: "Cover Banner", done: !!form.banner?.imageurl },
    { label: "Business Address", done: !!form.address?.street },
    { label: "Contact Info", done: !!form.contact?.phone },
    { label: "Brand Story", done: !!form.brandStory },
    { label: "Social Links", done: !!(form.socialLinks?.instagram || form.socialLinks?.facebook) },
  ];
  const completionDone = completionItems.filter((i) => i.done).length;
  const completionPct = Math.round((completionDone / completionItems.length) * 100);

  if (loading) {
    return (
      <div>
        <div className="skeleton" style={{ height: "32px", width: "200px", marginBottom: "8px" }} />
        <div className="skeleton" style={{ height: "16px", width: "280px", marginBottom: "24px" }} />
        <div className="skeleton" style={{ height: "450px", borderRadius: "12px" }} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4" style={{ paddingBottom: "20px", borderBottom: "1px solid var(--border)" }}>
        <div>
          <h2 style={{ fontSize: "22px", fontWeight: "900", color: "var(--dark)", margin: "0 0 4px" }}>
            My Store Profile
          </h2>
          <p style={{ fontSize: "14px", color: "var(--text-muted)", margin: 0 }}>
            Manage your public storefront, branding, addresses, and customer-facing identity.
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          {form.slug && (
            <a
              href={`https://www.imonline.shop/store/${form.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-secondary"
              style={{ padding: "9px 18px", fontSize: "13px", fontWeight: "700" }}
            >
              Preview Store ↗
            </a>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="btn btn-primary"
            style={{ padding: "9px 22px", fontSize: "13px", fontWeight: "800" }}
          >
            {submitting ? "Saving..." : "Save All Changes"}
          </button>
        </div>
      </div>

      {error && <div style={{ background: "#fee2e2", border: "1px solid #fecaca", borderRadius: "8px", padding: "14px 16px", color: "#dc2626", marginBottom: "20px" }}>{error}</div>}
      {success && <div style={{ background: "#dcfce7", border: "1px solid #bbf7d0", borderRadius: "8px", padding: "14px 16px", color: "#166534", marginBottom: "20px" }}>{success}</div>}

      {/* Completion Meter */}
      <div style={{ background: "#ffffff", border: "1px solid #e0e4ea", borderRadius: "12px", padding: "18px 22px", marginBottom: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <strong style={{ fontSize: "14px", color: "var(--dark)" }}>Store Profile Strength</strong>
          <span style={{ fontSize: "14px", fontWeight: "900", color: completionPct >= 80 ? "#16a34a" : "var(--primary)" }}>
            {completionPct}% Complete
          </span>
        </div>
        <div className="usage-bar-track" style={{ height: "8px", borderRadius: "99px", background: "#eef1f5" }}>
          <div
            className="usage-bar-fill"
            style={{ width: `${completionPct}%`, height: "100%", background: completionPct >= 80 ? "#16a34a" : "var(--primary)", borderRadius: "99px" }}
          />
        </div>
      </div>

      {/* Section Tabs */}
      <div className="vendor-tabs-scroll" style={{ display: "flex", gap: "6px", background: "#ffffff", padding: "6px", borderRadius: "10px", border: "1px solid #e0e4ea", marginBottom: "24px", overflowX: "auto" }}>
        {[
          { id: "basic", label: "Basic Info & Category" },
          { id: "branding", label: "Logo & Cover Banner" },
          { id: "gallery", label: "Store Gallery" },
          { id: "addresses", label: "Addresses & Shipping" },
          { id: "contact", label: "Contact & Hours" },
          { id: "social", label: "Social Presence" },
          { id: "story", label: "About Brand" },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveSection(tab.id)}
            style={{
              background: activeSection === tab.id ? "var(--dark)" : "transparent",
              color: activeSection === tab.id ? "#ffffff" : "var(--text-muted)",
              border: "none",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "13px",
              fontWeight: "800",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Form Content */}
      <form onSubmit={handleSubmit}>
        {/* ── 1. BASIC INFO ── */}
        {activeSection === "basic" && (
          <div className="stat-card" style={{ background: "#ffffff", padding: "26px", borderRadius: "12px", border: "1px solid #e0e4ea" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "900", color: "var(--dark)", marginBottom: "18px" }}>Store Identity &amp; Classification</h3>

            {/* Blue Badge / Verified Trusted Status */}
            <div style={{
              background: (form.isVerified || form.isTrusted) ? "#eff6ff" : "#f8fafc",
              border: (form.isVerified || form.isTrusted) ? "1.5px solid #bfdbfe" : "1px solid #e2e8f0",
              borderRadius: "10px",
              padding: "14px 18px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "10px"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "8px",
                  background: (form.isVerified || form.isTrusted) ? "#dbeafe" : "#f1f5f9",
                  color: (form.isVerified || form.isTrusted) ? "#2563eb" : "#64748b",
                  display: "grid",
                  placeItems: "center"
                }}>
                  <ShieldCheckIcon size={20} />
                </div>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: "800", color: (form.isVerified || form.isTrusted) ? "#1e40af" : "#334155" }}>
                    {(form.isVerified || form.isTrusted) ? "✓ Trusted Seller Blue Badge: Active" : "Trusted Seller Blue Badge: Pending Verification"}
                  </div>
                  <div style={{ fontSize: "12px", color: (form.isVerified || form.isTrusted) ? "#2563eb" : "#64748b", marginTop: "2px" }}>
                    {(form.isVerified || form.isTrusted)
                      ? "Your store displays the official blue verification badge on your marketplace storefront."
                      : "Blue badges are exclusively provided to trusted & vetted sellers on IM Online."}
                  </div>
                </div>
              </div>
              {(form.isVerified || form.isTrusted) && (
                <span style={{ background: "#2563eb", color: "#ffffff", fontSize: "11px", fontWeight: "800", padding: "4px 10px", borderRadius: "99px" }}>
                  ✓ Verified Badge Active
                </span>
              )}
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Store Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="e.g. FashionHub"
                  style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "14px" }}
                />
              </div>

              <div className="col-md-6">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Store URL Handle</label>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ background: "#f0f2f5", padding: "10px 12px", border: "1.5px solid #d0d7de", borderRight: "none", borderRadius: "8px 0 0 8px", fontSize: "13px", color: "var(--text-muted)" }}>
                    imonline.shop/store/
                  </span>
                  <input
                    type="text"
                    value={form.slug}
                    readOnly
                    placeholder="auto-generated"
                    style={{ flex: 1, padding: "10px 14px", borderRadius: "0 8px 8px 0", border: "1.5px solid #d0d7de", fontSize: "14px", background: "#fbfaf8" }}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Primary Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "14px", fontWeight: "700" }}
                >
                  {form.category && !categories.includes(form.category) && (
                    <option value={form.category}>{form.category}</option>
                  )}
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Business Type</label>
                <select
                  value={form.businessType}
                  onChange={(e) => setForm({ ...form, businessType: e.target.value })}
                  style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "14px", fontWeight: "700" }}
                >
                  <option value="Physical Store">Physical Store</option>
                  <option value="Home-based">Home-based Enterprise</option>
                  <option value="Warehouse">Warehouse &amp; Logistics</option>
                  <option value="Manufacturer">Manufacturer / Brand Owner</option>
                  <option value="Office">Office</option>
                </select>
              </div>

              <div className="col-12">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Store Tagline / Description</label>
                <textarea
                  rows="3"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief summary of your products and specialty for customers..."
                  style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "14px" }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── 2. BRANDING & IMAGES ── */}
        {activeSection === "branding" && (
          <div className="stat-card" style={{ background: "#ffffff", padding: "26px", borderRadius: "12px", border: "1px solid #e0e4ea" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "900", color: "var(--dark)", marginBottom: "18px" }}>Store Logo &amp; Cover Banner</h3>

            <div className="row g-4">
              {/* Logo */}
              <div className="col-md-5">
                <label style={{ fontSize: "13px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "8px" }}>Store Logo</label>
                
                {/* Logo Mode Toggle */}
                <div style={{ display: "flex", gap: "6px", marginBottom: "12px" }}>
                  <button
                    type="button"
                    onClick={() => setLogoMode("upload")}
                    style={{
                      padding: "5px 12px",
                      fontSize: "12px",
                      fontWeight: 800,
                      borderRadius: "6px",
                      border: logoMode === "upload" ? "1.5px solid var(--primary)" : "1px solid #d0d7de",
                      background: logoMode === "upload" ? "#faedea" : "#ffffff",
                      color: logoMode === "upload" ? "var(--primary)" : "#475569",
                      cursor: "pointer"
                    }}
                  >
                    📁 Upload from Device
                  </button>
                  <button
                    type="button"
                    onClick={() => setLogoMode("url")}
                    style={{
                      padding: "5px 12px",
                      fontSize: "12px",
                      fontWeight: 800,
                      borderRadius: "6px",
                      border: logoMode === "url" ? "1.5px solid var(--primary)" : "1px solid #d0d7de",
                      background: logoMode === "url" ? "#faedea" : "#ffffff",
                      color: logoMode === "url" ? "var(--primary)" : "#475569",
                      cursor: "pointer"
                    }}
                  >
                    🔗 Image URL
                  </button>
                </div>

                <div style={{ display: "flex", gap: "16px", alignItems: "center", marginBottom: "12px" }}>
                  <div style={{ width: "80px", height: "80px", borderRadius: "14px", background: "#faedea", color: "var(--primary)", display: "grid", placeItems: "center", fontSize: "28px", fontWeight: "900", border: "1.5px solid #f9dede", overflow: "hidden", position: "relative" }}>
                    {uploadingLogo ? (
                      <div className="spinner-border spinner-border-sm text-brand-primary" role="status" />
                    ) : form.logo?.imageurl ? (
                      <img src={form.logo.imageurl} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      form.name ? form.name.charAt(0).toUpperCase() : "S"
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    {logoMode === "upload" ? (
                      <div>
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "8px 14px",
                            background: "#f8fafc",
                            border: "1.5px dashed #cbd5e1",
                            borderRadius: "8px",
                            fontSize: "12.5px",
                            fontWeight: 700,
                            color: "#1e293b",
                            cursor: uploadingLogo ? "not-allowed" : "pointer"
                          }}
                        >
                          <span>{uploadingLogo ? "Uploading..." : "📂 Choose Image from Gallery"}</span>
                          <input
                            type="file"
                            accept="image/*"
                            disabled={uploadingLogo}
                            onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], "logo")}
                            style={{ display: "none" }}
                          />
                        </label>
                        {form.logo?.imageurl && (
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, logo: { imageurl: "" } })}
                            style={{ display: "block", marginTop: "6px", background: "none", border: "none", color: "#dc2626", fontSize: "11.5px", fontWeight: 700, cursor: "pointer", padding: 0 }}
                          >
                            ✕ Remove Logo
                          </button>
                        )}
                      </div>
                    ) : (
                      <div>
                        <input
                          type="text"
                          placeholder="Logo Image URL (https://...)"
                          value={form.logo?.imageurl || ""}
                          onChange={(e) => setForm({ ...form, logo: { imageurl: e.target.value } })}
                          style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13px" }}
                        />
                      </div>
                    )}
                    <small style={{ color: "var(--text-muted)", fontSize: "11px", marginTop: "4px", display: "block" }}>Recommended: 400x400 square image (PNG/JPG/WEBP).</small>
                  </div>
                </div>
              </div>

              {/* Cover Banner */}
              <div className="col-md-7">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <label style={{ fontSize: "13px", fontWeight: "800", color: "var(--dark)", margin: 0 }}>Store Cover Banner</label>
                  {/* Banner Mode Toggle */}
                  <div style={{ display: "flex", gap: "6px" }}>
                    <button
                      type="button"
                      onClick={() => setBannerMode("upload")}
                      style={{
                        padding: "4px 10px",
                        fontSize: "11.5px",
                        fontWeight: 800,
                        borderRadius: "6px",
                        border: bannerMode === "upload" ? "1.5px solid var(--primary)" : "1px solid #d0d7de",
                        background: bannerMode === "upload" ? "#faedea" : "#ffffff",
                        color: bannerMode === "upload" ? "var(--primary)" : "#475569",
                        cursor: "pointer"
                      }}
                    >
                      📁 Upload from Device
                    </button>
                    <button
                      type="button"
                      onClick={() => setBannerMode("url")}
                      style={{
                        padding: "4px 10px",
                        fontSize: "11.5px",
                        fontWeight: 800,
                        borderRadius: "6px",
                        border: bannerMode === "url" ? "1.5px solid var(--primary)" : "1px solid #d0d7de",
                        background: bannerMode === "url" ? "#faedea" : "#ffffff",
                        color: bannerMode === "url" ? "var(--primary)" : "#475569",
                        cursor: "pointer"
                      }}
                    >
                      🔗 Image URL
                    </button>
                  </div>
                </div>

                <div style={{ height: "130px", borderRadius: "12px", background: "#fbfaf8", border: "1.5px dashed #d0d7de", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", marginBottom: "10px", position: "relative" }}>
                  {uploadingBanner ? (
                    <div style={{ textAlign: "center" }}>
                      <div className="spinner-border text-brand-primary" role="status" />
                      <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "6px", fontWeight: 700 }}>Uploading banner...</div>
                    </div>
                  ) : form.banner?.imageurl ? (
                    <img src={form.banner.imageurl} alt="Banner" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <span style={{ color: "var(--text-muted)", fontSize: "13px", fontWeight: "700" }}>Banner Preview (1200x400)</span>
                  )}
                </div>

                {bannerMode === "upload" ? (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "8px 16px",
                        background: "#f8fafc",
                        border: "1.5px solid #cbd5e1",
                        borderRadius: "8px",
                        fontSize: "12.5px",
                        fontWeight: 700,
                        color: "#1e293b",
                        cursor: uploadingBanner ? "not-allowed" : "pointer"
                      }}
                    >
                      <span>📂 Select Banner Image from Gallery / PC</span>
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploadingBanner}
                        onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], "banner")}
                        style={{ display: "none" }}
                      />
                    </label>
                    {form.banner?.imageurl && (
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, banner: { imageurl: "" } })}
                        style={{ background: "none", border: "none", color: "#dc2626", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}
                      >
                        ✕ Remove Banner
                      </button>
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    placeholder="Cover Banner Image URL (https://...)"
                    value={form.banner?.imageurl || ""}
                    onChange={(e) => setForm({ ...form, banner: { imageurl: e.target.value } })}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13px" }}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── 3. STORE GALLERY ── */}
        {activeSection === "gallery" && (
          <div className="stat-card" style={{ background: "#ffffff", padding: "26px", borderRadius: "12px", border: "1px solid #e0e4ea" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "18px", flexWrap: "wrap", gap: "10px" }}>
              <div>
                <h3 style={{ fontSize: "18px", fontWeight: "900", color: "var(--dark)", margin: "0 0 4px" }}>Store Photo Gallery</h3>
                <p style={{ color: "var(--text-muted)", fontSize: "13px", margin: 0 }}>
                  Showcase your storefront, workshop, team, and interior to build buyer trust.
                </p>
              </div>

              {/* Gallery Mode Toggle */}
              <div style={{ display: "flex", gap: "6px" }}>
                <button
                  type="button"
                  onClick={() => setGalleryMode("upload")}
                  style={{
                    padding: "6px 12px",
                    fontSize: "12px",
                    fontWeight: 800,
                    borderRadius: "6px",
                    border: galleryMode === "upload" ? "1.5px solid var(--primary)" : "1px solid #d0d7de",
                    background: galleryMode === "upload" ? "#faedea" : "#ffffff",
                    color: galleryMode === "upload" ? "var(--primary)" : "#475569",
                    cursor: "pointer"
                  }}
                >
                  📁 Upload Photo File
                </button>
                <button
                  type="button"
                  onClick={() => setGalleryMode("url")}
                  style={{
                    padding: "6px 12px",
                    fontSize: "12px",
                    fontWeight: 800,
                    borderRadius: "6px",
                    border: galleryMode === "url" ? "1.5px solid var(--primary)" : "1px solid #d0d7de",
                    background: galleryMode === "url" ? "#faedea" : "#ffffff",
                    color: galleryMode === "url" ? "var(--primary)" : "#475569",
                    cursor: "pointer"
                  }}
                >
                  🔗 Add via URL
                </button>
              </div>
            </div>

            {/* Input / Upload Bar */}
            <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "10px", border: "1px solid #edf0f4", marginBottom: "20px" }}>
              <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                {galleryMode === "upload" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 2, minWidth: "240px" }}>
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "8px 14px",
                        background: "#ffffff",
                        border: "1.5px dashed #cbd5e1",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#1e293b",
                        cursor: uploadingGallery ? "not-allowed" : "pointer",
                        whiteSpace: "nowrap"
                      }}
                    >
                      <span>{uploadingGallery ? "⏳ Uploading..." : "📂 Choose Photo from Gallery"}</span>
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploadingGallery}
                        onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], "gallery")}
                        style={{ display: "none" }}
                      />
                    </label>
                    {newGalleryUrl && (
                      <span style={{ fontSize: "12px", color: "#16a34a", fontWeight: 700 }}>
                        ✓ Photo Ready
                      </span>
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    placeholder="Image URL (https://...)"
                    value={newGalleryUrl}
                    onChange={(e) => setNewGalleryUrl(e.target.value)}
                    style={{ flex: 2, minWidth: "220px", padding: "8px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13px", background: "#ffffff" }}
                  />
                )}

                <input
                  type="text"
                  placeholder="Photo Caption (e.g., Main Storefront, Workshop)"
                  value={newGalleryTitle}
                  onChange={(e) => setNewGalleryTitle(e.target.value)}
                  style={{ flex: 1.5, minWidth: "160px", padding: "8px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13px", background: "#ffffff" }}
                />

                <button
                  type="button"
                  onClick={addGalleryImage}
                  disabled={!newGalleryUrl.trim() || uploadingGallery}
                  className="btn btn-primary"
                  style={{ padding: "8px 18px", fontSize: "13px", fontWeight: 800, whiteSpace: "nowrap" }}
                >
                  + Add to Gallery
                </button>
              </div>
            </div>

            {/* Gallery Grid */}
            {form.gallery.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "14px" }}>
                {form.gallery.map((item, idx) => (
                  <div key={idx} style={{ background: "#fbfaf8", border: "1px solid #edf0f4", borderRadius: "10px", overflow: "hidden", position: "relative", boxShadow: "0 2px 6px rgba(0,0,0,0.03)" }}>
                    <img src={item.imageurl} alt={item.title} style={{ width: "100%", height: "120px", objectFit: "cover" }} />
                    <div style={{ padding: "8px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "12px", fontWeight: "700", color: "var(--dark)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</span>
                      <button type="button" onClick={() => removeGalleryImage(idx)} title="Delete Photo" style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: "14px", fontWeight: 800 }}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "28px", background: "#f8fafc", borderRadius: "10px", color: "var(--text-muted)", fontSize: "13px" }}>
                No photos in gallery yet. Upload photos of your shop front, shelves, or workspace above.
              </div>
            )}
          </div>
        )}

        {/* ── 4. ADDRESSES ── */}
        {activeSection === "addresses" && (
          <div className="stat-card" style={{ background: "#ffffff", padding: "26px", borderRadius: "12px", border: "1px solid #e0e4ea" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "900", color: "var(--dark)", marginBottom: "18px" }}>Addresses &amp; Logistics</h3>

            {/* GPS Location Auto-Detection Banner */}
            <div style={{
              background: "#f0fdf4",
              border: "1.5px solid #bbf7d0",
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "22px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "12px"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  background: "#dcfce7",
                  color: "#166534",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0
                }}>
                  <MapPinIcon size={20} />
                </div>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: "800", color: "#14532d" }}>
                    Shop GPS Location Coordinates
                  </div>
                  <div style={{ fontSize: "12.5px", color: "#166534", marginTop: "2px" }}>
                    {gpsStatus === "detecting" && "Detecting exact shop GPS coordinates..."}
                    {gpsStatus === "detected" && (gpsMessage || `GPS Pinned: ${form.location?.coordinates?.[1]?.toFixed(4)}, ${form.location?.coordinates?.[0]?.toFixed(4)}`)}
                    {gpsStatus === "idle" && (
                      form.location?.coordinates?.length >= 2
                        ? `Current Coordinates: ${form.location.coordinates[1]?.toFixed(4)}° N, ${form.location.coordinates[0]?.toFixed(4)}° E`
                        : "Click detect to pin your shop location automatically."
                    )}
                    {gpsStatus === "error" && (gpsMessage || "Could not auto-detect GPS coordinates.")}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={detectStoreLocation}
                disabled={gpsStatus === "detecting"}
                style={{
                  background: "#ffffff",
                  border: "1.5px solid #86efac",
                  color: "#166534",
                  padding: "7px 14px",
                  borderRadius: "8px",
                  fontSize: "12.5px",
                  fontWeight: "800",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                <RefreshCwIcon size={14} className={gpsStatus === "detecting" ? "spin" : ""} />
                {gpsStatus === "detecting" ? "Detecting..." : "Auto-Detect Current Shop Location"}
              </button>
            </div>

            {/* Business Address */}
            <div style={{ marginBottom: "24px" }}>
              <h4 style={{ fontSize: "15px", fontWeight: "800", color: "var(--dark)", marginBottom: "10px" }}>Physical Storefront Address</h4>
              <div className="row g-3">
                <div className="col-12">
                  <input
                    type="text"
                    placeholder="Street Address, Building, Area"
                    value={form.address.street}
                    onChange={(e) => setForm({ ...form, address: { ...form.address, street: e.target.value } })}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                  />
                </div>
                <div className="col-md-4">
                  <input
                    type="text"
                    placeholder="City"
                    value={form.address.city}
                    onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                  />
                </div>
                <div className="col-md-4">
                  <input
                    type="text"
                    placeholder="State"
                    value={form.address.state}
                    onChange={(e) => setForm({ ...form, address: { ...form.address, state: e.target.value } })}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                  />
                </div>
                <div className="col-md-4">
                  <input
                    type="text"
                    placeholder="Pincode"
                    value={form.address.pincode}
                    onChange={(e) => setForm({ ...form, address: { ...form.address, pincode: e.target.value } })}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                  />
                </div>
              </div>
            </div>

            {/* Godown / Warehouse Address Section */}
            <div style={{
              marginTop: "20px",
              padding: "18px",
              borderRadius: "12px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              marginBottom: "24px"
            }}>
              <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", margin: 0 }}>
                <input
                  type="checkbox"
                  checked={form.godownAddress?.hasGodown}
                  onChange={(e) => setForm({
                    ...form,
                    godownAddress: { ...form.godownAddress, hasGodown: e.target.checked }
                  })}
                  style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "var(--primary)" }}
                />
                <div>
                  <strong style={{ fontSize: "14px", color: "#0f172a", display: "block" }}>
                    Add Separate Godown / Warehouse Address
                  </strong>
                  <span style={{ fontSize: "12px", color: "#64748b" }}>
                    Enable if your storage godown or courier dispatch facility is at a different location from your storefront.
                  </span>
                </div>
              </label>

              {form.godownAddress?.hasGodown && (
                <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #e2e8f0" }}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label style={{ fontSize: "12px", fontWeight: "700", display: "block", marginBottom: "4px" }}>Godown Contact Person</label>
                      <input
                        type="text"
                        placeholder="e.g. Ramesh Kumar"
                        value={form.godownAddress?.contactName || ""}
                        onChange={(e) => setForm({
                          ...form,
                          godownAddress: { ...form.godownAddress, contactName: e.target.value }
                        })}
                        style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label style={{ fontSize: "12px", fontWeight: "700", display: "block", marginBottom: "4px" }}>Godown Phone</label>
                      <input
                        type="tel"
                        maxLength="10"
                        placeholder="10-digit mobile number"
                        value={form.godownAddress?.phone || ""}
                        onChange={(e) => setForm({
                          ...form,
                          godownAddress: { ...form.godownAddress, phone: e.target.value.replace(/\D/g, "") }
                        })}
                        style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                      />
                    </div>
                    <div className="col-12">
                      <label style={{ fontSize: "12px", fontWeight: "700", display: "block", marginBottom: "4px" }}>Godown Street Address</label>
                      <input
                        type="text"
                        placeholder="Godown No, Industrial Focal Point, Landmark"
                        value={form.godownAddress?.street || ""}
                        onChange={(e) => setForm({
                          ...form,
                          godownAddress: { ...form.godownAddress, street: e.target.value }
                        })}
                        style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                      />
                    </div>
                    <div className="col-md-4">
                      <input
                        type="text"
                        placeholder="Godown City"
                        value={form.godownAddress?.city || ""}
                        onChange={(e) => setForm({
                          ...form,
                          godownAddress: { ...form.godownAddress, city: e.target.value }
                        })}
                        style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                      />
                    </div>
                    <div className="col-md-4">
                      <input
                        type="text"
                        placeholder="Godown State"
                        value={form.godownAddress?.state || ""}
                        onChange={(e) => setForm({
                          ...form,
                          godownAddress: { ...form.godownAddress, state: e.target.value }
                        })}
                        style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                      />
                    </div>
                    <div className="col-md-4">
                      <input
                        type="text"
                        maxLength="6"
                        placeholder="Godown Pincode"
                        value={form.godownAddress?.pincode || ""}
                        onChange={(e) => setForm({
                          ...form,
                          godownAddress: { ...form.godownAddress, pincode: e.target.value.replace(/\D/g, "") }
                        })}
                        style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Courier Pickup Address */}
            <div style={{ borderTop: "1px solid #edf0f4", paddingTop: "18px", marginBottom: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <h4 style={{ fontSize: "15px", fontWeight: "800", color: "var(--dark)", margin: 0 }}>Courier Pickup Address</h4>
                <label style={{ fontSize: "12.5px", fontWeight: "700", color: "var(--dark)", display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={form.pickupAddress.isSameAsBusiness}
                    onChange={(e) => setForm({ ...form, pickupAddress: { ...form.pickupAddress, isSameAsBusiness: e.target.checked } })}
                  />
                  Same as Storefront Address
                </label>
              </div>
              {!form.pickupAddress.isSameAsBusiness && (
                <div className="row g-3">
                  <div className="col-md-6">
                    <input
                      type="text"
                      placeholder="Pickup Contact Name"
                      value={form.pickupAddress.contactName}
                      onChange={(e) => setForm({ ...form, pickupAddress: { ...form.pickupAddress, contactName: e.target.value } })}
                      style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                    />
                  </div>
                  <div className="col-md-6">
                    <input
                      type="text"
                      placeholder="Pickup Contact Phone"
                      value={form.pickupAddress.phone}
                      onChange={(e) => setForm({ ...form, pickupAddress: { ...form.pickupAddress, phone: e.target.value } })}
                      style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                    />
                  </div>
                  <div className="col-12">
                    <input
                      type="text"
                      placeholder="Pickup Street Address"
                      value={form.pickupAddress.street}
                      onChange={(e) => setForm({ ...form, pickupAddress: { ...form.pickupAddress, street: e.target.value } })}
                      style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── 5. CONTACT & HOURS ── */}
        {activeSection === "contact" && (
          <div className="stat-card" style={{ background: "#ffffff", padding: "26px", borderRadius: "12px", border: "1px solid #e0e4ea" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "900", color: "var(--dark)", marginBottom: "18px" }}>Business Contact &amp; Operating Hours</h3>

            {/* Live Operational Status Indicator */}
            {(() => {
              const now = new Date();
              const utc = now.getTime() + now.getTimezoneOffset() * 60000;
              const ist = new Date(utc + 5.5 * 3600000);
              const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
              const currentDay = days[ist.getDay()];
              const currentMinutes = ist.getHours() * 60 + ist.getMinutes();
              const [oh = 10, om = 0] = (form.openTime || "10:00").split(':').map(Number);
              const [ch = 20, cm = 0] = (form.closeTime || "20:00").split(':').map(Number);
              const openMin = oh * 60 + om;
              const closeMin = ch * 60 + cm;
              const isDayOpen = Array.isArray(form.operatingDays) && form.operatingDays.includes(currentDay);
              const isTimeOpen = currentMinutes >= openMin && currentMinutes < closeMin;
              const isLiveOpen = !form.isManualClosed && isDayOpen && isTimeOpen;

              return (
                <div style={{
                  padding: "14px 18px",
                  borderRadius: "10px",
                  background: isLiveOpen ? "#f0fdf4" : "#fef2f2",
                  border: isLiveOpen ? "1.5px solid #bbf7d0" : "1.5px solid #fecaca",
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "10px"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      background: isLiveOpen ? "#16a34a" : "#dc2626",
                      display: "inline-block"
                    }} />
                    <div>
                      <strong style={{ fontSize: "14px", color: isLiveOpen ? "#14532d" : "#991b1b" }}>
                        Store Live Status Right Now: {isLiveOpen ? "● Open Now" : "● Closed Now"}
                      </strong>
                      <div style={{ fontSize: "12px", color: isLiveOpen ? "#166534" : "#b91c1c", marginTop: "2px" }}>
                        {form.isManualClosed
                          ? "Store is temporarily set to Closed by you."
                          : !isDayOpen
                          ? `Closed on ${currentDay}s according to selected operating days.`
                          : !isTimeOpen
                          ? `Outside operating hours (${form.openTime || "10:00"} to ${form.closeTime || "20:00"} IST).`
                          : `Customers can visit and place local orders (closes at ${form.closeTime || "20:00"}).`}
                      </div>
                    </div>
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12.5px", fontWeight: "700", color: "#374151", cursor: "pointer", margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={form.isManualClosed}
                      onChange={(e) => setForm({ ...form, isManualClosed: e.target.checked })}
                    />
                    Temporarily Close Shop Today
                  </label>
                </div>
              );
            })()}

            <div className="row g-3">
              <div className="col-md-6">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Customer Support Email</label>
                <input
                  type="email"
                  value={form.contact.email}
                  onChange={(e) => setForm({ ...form, contact: { ...form.contact, email: e.target.value } })}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                />
              </div>

              <div className="col-md-6">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Customer Support Phone</label>
                <input
                  type="text"
                  value={form.contact.phone}
                  onChange={(e) => setForm({ ...form, contact: { ...form.contact, phone: e.target.value } })}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                />
              </div>

              {/* Structured Operating Hours */}
              <div className="col-md-6">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Shop Opening Time</label>
                <input
                  type="time"
                  value={form.openTime || "10:00"}
                  onChange={(e) => setForm({
                    ...form,
                    openTime: e.target.value,
                    businessHours: `${(form.operatingDays || []).join(', ')}: ${e.target.value} - ${form.closeTime || '20:00'}`
                  })}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                />
              </div>

              <div className="col-md-6">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Shop Closing Time</label>
                <input
                  type="time"
                  value={form.closeTime || "20:00"}
                  onChange={(e) => setForm({
                    ...form,
                    closeTime: e.target.value,
                    businessHours: `${(form.operatingDays || []).join(', ')}: ${form.openTime || '10:00'} - ${e.target.value}`
                  })}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                />
              </div>

              <div className="col-12">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "8px" }}>Operating Days</label>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => {
                    const isSelected = Array.isArray(form.operatingDays) && form.operatingDays.includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => {
                          const current = Array.isArray(form.operatingDays) ? [...form.operatingDays] : [];
                          const updated = isSelected ? current.filter(d => d !== day) : [...current, day];
                          setForm({ ...form, operatingDays: updated });
                        }}
                        style={{
                          padding: "6px 14px",
                          borderRadius: "8px",
                          border: isSelected ? "1.5px solid var(--primary)" : "1.5px solid #d0d7de",
                          background: isSelected ? "var(--primary)" : "#ffffff",
                          color: isSelected ? "#ffffff" : "#374151",
                          fontSize: "12.5px",
                          fontWeight: "800",
                          cursor: "pointer"
                        }}
                      >
                        {isSelected ? `✓ ${day}` : day}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="col-12">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Operating Hours Text (Displayed on Storefront)</label>
                <input
                  type="text"
                  placeholder="e.g. Mon - Sat: 10:00 AM - 8:00 PM"
                  value={form.businessHours}
                  onChange={(e) => setForm({ ...form, businessHours: e.target.value })}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── 6. SOCIAL LINKS ── */}
        {activeSection === "social" && (
          <div className="stat-card" style={{ background: "#ffffff", padding: "26px", borderRadius: "12px", border: "1px solid #e0e4ea" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "900", color: "var(--dark)", marginBottom: "18px" }}>Social Media Presence</h3>
            <p style={{ color: "var(--text-muted)", fontSize: "13px", marginTop: "-12px", marginBottom: "18px" }}>
              Optional &mdash; Connect your social channels so customers can explore and follow your brand.
            </p>

            <div className="row g-3">
              <div className="col-md-6">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Instagram Profile URL</label>
                <input
                  type="text"
                  placeholder="https://instagram.com/yourhandle"
                  value={form.socialLinks?.instagram || ""}
                  onChange={(e) => setForm({ ...form, socialLinks: { ...form.socialLinks, instagram: e.target.value } })}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                />
              </div>

              <div className="col-md-6">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Facebook Page URL</label>
                <input
                  type="text"
                  placeholder="https://facebook.com/yourpage"
                  value={form.socialLinks?.facebook || ""}
                  onChange={(e) => setForm({ ...form, socialLinks: { ...form.socialLinks, facebook: e.target.value } })}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                />
              </div>

              <div className="col-md-6">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>YouTube Channel URL</label>
                <input
                  type="text"
                  placeholder="https://youtube.com/@yourchannel"
                  value={form.socialLinks?.youtube || ""}
                  onChange={(e) => setForm({ ...form, socialLinks: { ...form.socialLinks, youtube: e.target.value } })}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                />
              </div>

              <div className="col-md-6">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Official Website URL</label>
                <input
                  type="text"
                  placeholder="https://yourbrand.com"
                  value={form.socialLinks?.website || ""}
                  onChange={(e) => setForm({ ...form, socialLinks: { ...form.socialLinks, website: e.target.value } })}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── 7. ABOUT BRAND ── */}
        {activeSection === "story" && (
          <div className="stat-card" style={{ background: "#ffffff", padding: "26px", borderRadius: "12px", border: "1px solid #e0e4ea" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "900", color: "var(--dark)", marginBottom: "18px" }}>Brand Story &amp; Heritage</h3>

            <div className="row g-3">
              <div className="col-md-6">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Years in Business</label>
                <input
                  type="number"
                  min="0"
                  value={form.yearsInBusiness}
                  onChange={(e) => setForm({ ...form, yearsInBusiness: e.target.value })}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                />
              </div>

              <div className="col-md-6">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Specialization / USP</label>
                <input
                  type="text"
                  placeholder="e.g. Handcrafted Pure Cotton Ethnic Wear"
                  value={form.specialization}
                  onChange={(e) => setForm({ ...form, specialization: e.target.value })}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                />
              </div>

              <div className="col-12">
                <label style={{ fontSize: "12px", fontWeight: "800", color: "var(--dark)", display: "block", marginBottom: "6px" }}>Our Brand Story</label>
                <textarea
                  rows="4"
                  placeholder="Tell your customers about your roots, craftsmanship, quality philosophy, and what makes your products unique..."
                  value={form.brandStory}
                  onChange={(e) => setForm({ ...form, brandStory: e.target.value })}
                  style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #d0d7de", fontSize: "13.5px" }}
                />
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary"
            style={{ padding: "10px 24px", fontSize: "14px", fontWeight: "800" }}
          >
            {submitting ? "Saving Store Profile..." : "Save All Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default StorePage;
